import React, {
  useEffect, useMemo, useState, useCallback,
} from 'react';
import Head from 'next/head';
import getConfig from 'next/config';

import { logError } from 'lib/datadog';
import { GridStack } from 'components/GridStack';
import { VideoUnmuteButton } from 'components/VideoUnmuteButton';
// eslint-disable-next-line import/no-unresolved
import { Gamma } from '@nbcnews/gamma';
import { stub as $t } from '@nbcnews/analytics-framework';
import classNames from 'classnames';
import BrowserDetection from 'lib/BrowserDetection';
import { isVideoLive } from 'lib/videoUtils';
import { getFeatureConfigForBrand } from 'lib/getFeatureStatus';
import { VerticalContext, BreakpointContext } from 'lib/ContextTypes';
import { USE_MPX_VIDEO_PARAMS, ENABLE_RETINA } from 'lib/brandFeatures';
// eslint-disable-next-line import/no-unresolved
import { useConsentStatus } from '@nbcnews/gpp-utilities';
import { JW_PLAYER_LIBRARY_URL } from 'lib/constants';
import { useCombinedCallbacks } from 'lib/Hooks/useCombinedCallbacks';
import MobileApi from 'lib/MobileApi';
import { useShouldRestrictVideoAdsByGeoLocation } from 'lib/Hooks/useShouldRestrictVideoAdsByGeoLocation';
import { useRerender } from 'lib/Hooks/useRerender';
import { useShouldRemoveAds } from 'lib/subscriptionUtils';
import { useVideoControl } from './useVideoControl';
import { Slate } from './Slate';
import { VideoInfoStoryline } from './VideoInfoStoryline';
import { VideoInfo } from './VideoInfo';
import { selectVideoData } from './gammaUtils/selectVideoData';
import { usePlaylistAdapter } from './gammaUtils/usePlaylistAdapter';
import { useAdConfigurator } from './gammaUtils/getAdvertisingConfig';

import styles from './styles.module.scss';

const {
  publicRuntimeConfig: { JW_PLAYER_LIBRARY_KEY },
} = getConfig();

/**
 *
 */
const emptyFunction = () => {};

const floatEnabled = {};

/**
 * @typedef {import('@nbcnews/gamma').GammaProps} GammaProps
 * @typedef {GammaProps["playlistItem"]} PlaylistItem
 * @typedef {import('./gammaUtils/usePlaylistAdapter').Playlist} Playlist
 * @typedef {import('./gammaUtils/selectVideoData').VideoData} VideoData
 * @typedef { VideoData & {
 *   associatedVideoPlaylist?: Playlist;
 * }} Video
 */

/**
 * @param {object} props
 * @param {boolean} [props.autoPlay]
 * @param {boolean} [props.isStoryline]
 * @param {boolean} [props.showHeadlineInlineVid]
 * @param {string} [props.inlineHeadlineOverride]
 * @param {boolean} [props.isEmbedded]
 * @param {boolean} [props.useEmbeddedAds]
 * @param {boolean} [props.continuousPlay]
 * @param {boolean} [props.replay]
 * @param {Playlist} [props.playlist]
 * @param {() => void} [props.onAdPlay]
 * @param {(selected: Video, playlistIndex: number, playlist: Playlist) => void} [props.onVideoEnd]
 * @param {() => void} [props.onVideoPlay]
 * @param {boolean} [props.disableSticky]
 * @param {boolean} [props.disableStickyOnMute]
 * @param {boolean} [props.onlyAutoplayMuted]
 * @param {"continuous" | "manual"} [props.nowPlayingInitiator]
 * @param {boolean | undefined} [props.hideVideoTitle]
 * @param {boolean} [props.hidePublishedDate]
 * @param {VideoData} props.video
 */
export function GammaVideoPlayer({
  autoPlay,
  isStoryline,
  showHeadlineInlineVid = true,
  hideVideoTitle,
  inlineHeadlineOverride,
  hidePublishedDate,
  isEmbedded = false,
  useEmbeddedAds = false,
  continuousPlay,
  replay,
  playlist,
  onAdPlay = emptyFunction,
  onVideoEnd = emptyFunction,
  onVideoPlay = emptyFunction,
  disableSticky,
  disableStickyOnMute,
  onlyAutoplayMuted,
  nowPlayingInitiator,
  video,
}) {
  const { data: gppStatus } = useConsentStatus();
  const vertical = React.useContext(VerticalContext);
  const { isM, isL, isXL } = React.useContext(BreakpointContext);
  const useMpxParams = getFeatureConfigForBrand(USE_MPX_VIDEO_PARAMS, vertical);
  const enableRetina = getFeatureConfigForBrand(ENABLE_RETINA, vertical);
  const isLive = isVideoLive(video);
  /** @type {GammaProps["jwPlayerRef"]} */
  const jwRef = React.useRef(null);

  const shouldRemoveAdsBySubscription = useShouldRemoveAds();

  const shouldRestrictVideoAdsByGeoLocation = useShouldRestrictVideoAdsByGeoLocation();

  const [gammaKey, rerenderGammaPlayer] = useRerender();

  const {
    title,
    datePublished,
    isExpired,
    duration,
    videoUrl,
    teaseImageUrl,
    teaseImageAltText,
    playlistItem,
    videoId,
    publisherName,
  } = selectVideoData(video, {
    useMpxParams,
    isLive,
    vertical,
    isEmbedded: useEmbeddedAds,
    gppStatus,
    breakpoints: { isM, isL, isXL },
    enableRetina,
  });

  const adConfigurator = useAdConfigurator({
    publisherName,
    isEmbedded: useEmbeddedAds,
    isLiveVideo: isLive,
    gppStatus,
  });

  const [canDisplayMuteBlock, setCanDisplayMuteBlock] = useState(true);
  const analyticsMetadata = useMemo(
    () => ({
      ...video,
      autoPlay,
      continuousPlay,
      nowPlayingInitiator,
    }),
    [video, autoPlay, continuousPlay, nowPlayingInitiator],
  );

  useEffect(() => {
    if (
      (shouldRemoveAdsBySubscription && jwRef.current?.getConfig().advertising)
      || (!shouldRemoveAdsBySubscription && !jwRef.current?.getConfig().advertising)
    ) {
      rerenderGammaPlayer();
    }
  }, [shouldRemoveAdsBySubscription]);

  // Temporary fix for a browser issue with safari that if a user mutes via our mute block will lock the player in a nonrecoverable state
  useEffect(() => {
    if (BrowserDetection.isSafari() && BrowserDetection.version() === 16) {
      setCanDisplayMuteBlock(false);
    } else {
      setCanDisplayMuteBlock(true);
    }
  }, []);

  const [hasError, setHasError] = useState(false);

  const onNewPlaylistItem = useCallback(() => {
    setHasError(false);
  }, []);

  const onGammaError = useCallback((error) => {
    const jsErr = new Error('Gamma Error from JWPlayer');
    logError(jsErr, error);
    setHasError(true);
    // eslint-disable-next-line no-console
    console.error('Gamma Error', error);
  }, []);

  // Useful for scanning all JWPlayerEvents. Gamma prop is `onAll`
  // const onGammaEvent = useCallback((event, context) => {
  //   // eslint-disable-next-line no-console
  //   console.log('(g.vp.i153.x) Gamma Event', event, context);
  // }, []);

  const videoControl = useVideoControl({
    autoPlay: (() => {
      /**
       * Don't autoplay if the video is expired, since it will play an ad before
       * JW Player displays the error message
       * @see https://nbcnewsdigital.atlassian.net/browse/NGSP-825
       */
      if (isExpired) return false;
      if (autoPlay && onlyAutoplayMuted) return 'muted';
      return Boolean(autoPlay);
    })(),
    relayEvents: {
      /**
       *
       * @param {boolean} isPaused
       */
      onPauseChange: (/** @type {boolean} */ isPaused) => {
        if (!isPaused) onVideoPlay();
      },
      /**
       *
       */
      onAdImpression() {
        onAdPlay();
      },
      onPlaylistItem: onNewPlaylistItem,
      onError: onGammaError,
      // onAll: onGammaEvent, // useful for devs, see comment above
    },
  });

  const {
    isPlayComplete,
    handleVideoEnd,
    handlePlaylistItem: playlistAdapterItemHandler,
  } = usePlaylistAdapter({
    playlist,
    replay,
    continuousPlay,
    videoId,
    onVideoEnd,
  });

  const handlePlaylistItem = useCombinedCallbacks([
    playlistAdapterItemHandler,
    videoControl.state.onPlaylistItem,
    onNewPlaylistItem,
  ]);

  const disableFloat = disableSticky || (disableStickyOnMute && videoControl.state.isMuted);

  return (
    <GridStack
      data-testid={autoPlay ? 'gamma-video-player-autoplay' : 'gamma-video-player'}
      className={classNames(`${styles.videoContainer} video-container`, {
        paused: videoControl.state.isPaused,
        started: videoControl.state.hasPlaybackStarted,
      })}
    >
      <Head>
        <link
          key="jw-player-library-preload"
          rel="preload"
          as="script"
          href={JW_PLAYER_LIBRARY_URL}
        />
      </Head>
      <div id="gamma-accessibility-wrapper" aria-hidden={!videoControl.state.hasPlaybackStarted}>
        <Gamma
          key={gammaKey}
          jwPlayerRef={jwRef}
          jwPlayerLibraryUrl={JW_PLAYER_LIBRARY_URL}
          jwPlayerLibraryKey={JW_PLAYER_LIBRARY_KEY}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...videoControl.state}
          $t={$t}
          $tMetadata={analyticsMetadata}
          config={{
            skin: {
              name: 'gamma',
            },
            renderCaptionsNatively: false,
          }}
          // Overridden videoControl props
          playlistItem={playlistItem}
          onVideoEnd={handleVideoEnd}
          onPlaylistItem={handlePlaylistItem}
          floating={disableFloat ? undefined : floatEnabled}
          advertising={(shouldRemoveAdsBySubscription || shouldRestrictVideoAdsByGeoLocation)
            ? undefined
            : adConfigurator}
        />
      </div>
      <Slate
        teaseImageUrl={teaseImageUrl}
        teaseImageAltText={teaseImageAltText}
        isPaused={videoControl.state.isPaused}
        isExpired={isExpired}
        isHidden={hasError}
        hasPlaybackStarted={videoControl.state.hasPlaybackStarted}
        lastVideoIsEnded={isPlayComplete}
        videoHref={videoUrl}
        onPlayClick={() => {
          const result = MobileApi.viewVideo(video.id);
          if (typeof result !== 'string') {
            videoControl.state.onPauseChange(false);
            // Ensures that for keyboard users, the video container is focused
            // after the play button is clicked.
            const videoContainer = jwRef?.current?.getContainer();
            if (videoContainer) {
              videoContainer.focus();
            }
          }
        }}
        onReplayClick={() => videoControl.control.seek(0)}
        title={inlineHeadlineOverride || title}
        showHeadlineInlineVid={showHeadlineInlineVid}
        isStoryline={isStoryline}
      />
      <div className={`info-layer ${styles.infoLayer}`}>
        <VideoUnmuteButton
          show={
            canDisplayMuteBlock
            && videoControl.state.isMuted
            && videoControl.state.hasPlaybackStarted
          }
          onClickUnmute={() => videoControl.state.onMuteChange(false)}
        />
        {isStoryline
          ? (
            <VideoInfoStoryline
              title={inlineHeadlineOverride || title}
              hideVideoTitle={false}
              date={hidePublishedDate ? undefined : datePublished}
              duration={duration}
              href={videoUrl}
              isEmbedded={isEmbedded}
              forceShow={videoControl.state.isPaused || isPlayComplete}
              isStoryline={isStoryline}
              videoState={videoControl.state}
            />
          )
          : (
            <VideoInfo
              title={title}
              hideVideoTitle={hideVideoTitle}
              date={hidePublishedDate ? undefined : datePublished}
              duration={duration}
              href={videoUrl}
              isEmbedded={isEmbedded}
              forceShow={videoControl.state.isPaused || isPlayComplete}
            />
          )}
      </div>
    </GridStack>
  );
}
